import { Button, Input, Tabs, TabItem, ProgressBar } from 'components/atoms/index';
import * as styles from 'components/organisms/Table/Table.css'
import { Icon } from '@/icons';
import { vars } from 'style/theme.css'
import { useLocation, useNavigate } from 'react-router-dom';
import Pagination from 'components/atoms/Pagination/Pagination';
import { useState } from 'react';
import { getSlugPrefix } from '@/utils/slug';
import { formatNumber } from '@/utils/number';

interface Vocabulary {
    id: number
    name: string
    wordCount: number
    learningHistory: string
    registrationDate: string
  }

interface VocabularyTableProps {
  data?: Vocabulary[];
  searchText?: string;
  onSearchChange?: (text: string) => void;
  onSearch?: () => void;
  currentPage?: number;
  totalElements?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  loading?: boolean;
  disableNavigation?: boolean;
}
  
  // 컬럼 렌더링용 메타 정보
  const columns = [
    { key: 'id'     as const, render: (row: Vocabulary) => row.id },
    { key: 'name'   as const, render: (row: Vocabulary) => row.name },
    { key: 'wordCount' as const, render: (row: Vocabulary) => (
      <div className="flex gap-2 items-center lg:block">
        <span className="text-xs text-gray-400 lg:hidden">학습 단어 수</span>
        <span>{formatNumber(row.wordCount ?? 0)}개</span>
      </div>
    ) },
    { key: 'learningHistory' as const, render: (row: Vocabulary) => {
      const getStatusColor = (status: string) => {
        switch (status) {
          case '학습 중':
            return 'text-orange-500';
          case '학습 대기':
            return 'text-blue-500';
          case '학습 완료':
            return 'text-green-500';
          default:
            return 'text-gray-500';
        }
      };
      
      return (
        <div className="flex gap-2 items-center lg:block">
          <span className={getStatusColor(row.learningHistory)}>{row.learningHistory}</span>
        </div>
      );
    } },
    { key: 'registrationDate' as const, render: (row: Vocabulary) => (
      <div className="flex gap-2 items-center lg:block">
        <span className="text-xs text-gray-400 lg:hidden">등록일</span>
        <span>{row.registrationDate}</span>
      </div>
    ) },
  ]
  


export default function VocabularyTable({
  data = [],
  searchText = '',
  onSearchChange,
  onSearch,
  currentPage = 1,
  totalElements = 0,
  pageSize = 10,
  onPageChange,
  loading = false,
  disableNavigation = false
}: VocabularyTableProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const slugPrefix = getSlugPrefix(location.pathname);

  const handleRowClick = (id: number) => {
    if (disableNavigation) return;
    navigate(`${slugPrefix}/vocabulary/book?wordbookId=${id}`);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onSearch?.();
    }
  };

  return (
        <main className='flex flex-col w-full'>
            <section className='flex flex-col gap-2 items-start pt-3 w-full lg:px-5 lg:flex-row lg:justify-between lg:items-center'>
                <Input
                placeholder='검색어를 입력해주세요.'
                className='max-w-[500px]'
                value={searchText}
                onChange={(e) => onSearchChange?.(e.target.value)}
                onKeyDown={handleKeyDown}
                icon={<Icon name="search" size={20} color={vars.color.gray[300]} />}
                />
            </section>

            <section className='flex flex-col flex-1 justify-between pt-5 w-full'>
                <div className='w-full'>
                    {loading ? (
                      <div className='py-12 text-center text-gray-500'>
                        로딩 중...
                      </div>
                    ) : data.length === 0 ? (
                      <div className='py-12 text-center text-gray-500'>
                        배정된 학습이 없습니다.
                      </div>
                    ) : (
                      <>
                        <ul className={`${styles.headerList} w-full`}>
                            <li className={`${styles.headerItem} max-w-[80px] justify-center`}>순번</li>
                            <li className={`${styles.headerItem}`}>단어장명</li>
                            <li className={`${styles.headerItem} max-w-[200px]`}>학습 단어 수</li>
                            <li className={`${styles.headerItem} max-w-[200px]`}>학습 이력</li>
                            <li className={`${styles.headerItem} max-w-[200px]`}>등록일</li>
                        </ul>
                      <div className='flex flex-col gap-4 w-full lg:gap-0'>
                          {data.map((row, index) => {
                            const globalIndex = (currentPage - 1) * pageSize + index;
                            const totalCount = totalElements || data.length;
                            const descendingNo = totalCount > 0 ? totalCount - globalIndex : data.length - index;
                            const displayNo = descendingNo > 0 ? descendingNo : Math.max(1, data.length - index);
                            return (
                              <ul 
                                  key={row.id} 
                                  className={`${styles.bodyList} lg:shadow-none table-card ${disableNavigation ? '' : 'cursor-pointer hover:bg-gray-50'} transition-colors !grid grid-cols-2 lg:!flex`}
                                  onClick={() => handleRowClick(row.id)}
                              >
                              {columns.map(({ key, render }) => (
                                  <li
                                  key={key}
                                  className={`${styles.bodyItem} ${
                                      key === 'id' ? 'max-w-[80px] !hidden lg:!flex justify-center' :
                                      key === 'name' ? 'col-span-2 lg:col-span-1 ' :
                                      key === 'wordCount' ? 'text-sm text-gray-500 max-w-[200px] lg:static col-span-1' :
                                      key === 'learningHistory' ? 'text-sm text-gray-500 max-w-[200px] lg:static col-span-1 absolute top-5 right-5' :
                                      key === 'registrationDate' ? 'text-sm text-gray-500 max-w-[200px] lg:static col-span-1' :
                                      ''
                                  }`}
                                  >
                                  {key === 'id' ? formatNumber(displayNo) : render(row)}
                                  </li>
                              ))}
                              </ul>
                          )})}
                      </div>
                      </>
                    )}
                </div>
                <div className='py-5'>
                  <Pagination
                  total={totalElements}
                  pageSize={pageSize}
                  current={currentPage}
                  onChange={(page) => onPageChange?.(page)}
                  />
                </div>
            </section>
        </main>
  )
}